// 接手頁（`/org/kmgr/handover`；SPEC #kinio-kms-handover「接手頁」、D37–D39；k1-wbs M5-03）：代建者與接手客戶看同一頁，各自那一步才有按鈕。
// 一頁依序打勾，前一步完成才開下一步：① 擁有權（代建者交）② 資料處理協議（客戶）③ 委派給安裝商（客戶，可略過）④ 監控範本（客戶）
// ⑤ 客戶的金鑰（客戶請、代建者簽）⑥ 移除代建者的金鑰（代建者）⑦ 代建者退出。平台的動作（邀請、擁有權、退出）打 app-api，其餘打 kmgr。
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useApp } from '../../../lib/AppContext';
import { orgApi, orgsApi, type MyOrg } from '../../../lib/planeApi';
import { stamp } from '../../../lib/timefmt';
import { useConfirm } from '../../../components/ConfirmDialog';
import { SkeletonLines } from '../../../components/Skeleton';
import { kmgrApi } from '../api';
import { errText, NS, StateBox } from '../parts';
import { requestSignature } from '../signWindow';
import type { KeysetAssertion } from '../types';
import { handoverApi } from './api';
import { myTurn } from './model';
import type { Handover, HandoverStep } from './types';
import { useEmailVerifyPrompt } from '../../../components/EmailVerifyPrompt';

const ICON: Record<HandoverStep['state'], string> = { done: '✓', skipped: '–', todo: '●', waiting: '○' };

export function HandoverPage({ org }: { org: MyOrg }) {
  const { t } = useTranslation(NS);
  const q = useQuery({ queryKey: ['kmgr', 'handover', org.org_id], queryFn: () => handoverApi.get(org.api, org.org_id), retry: 0, refetchInterval: 15_000 });
  if (q.isLoading) return <SkeletonLines n={6} label={t('common.loading')} />;
  if (q.isError && isKinioError(q.error) && q.error.code === 'NOT_FOUND')
    return <StateBox icon="✓" title={t('handover.not_built')} body={t('handover.not_built_body')} testId="km-handover-none" />;
  if (!q.data)
    return (
      <StateBox icon="⊘" title={t('handover.unavailable')} body={errText(t, q.error)}>
        <button className="btn" onClick={() => void q.refetch()}>
          {t('common.retry')}
        </button>
      </StateBox>
    );
  return <HandoverBody org={org} h={q.data} />;
}

function HandoverBody({ org, h }: { org: MyOrg; h: Handover }) {
  const { t } = useTranslation(NS);
  const { config } = useApp();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { ask, dialog } = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const verify = useEmailVerifyPrompt(); // 邀請客戶管理員遇 403 EMAIL_NOT_VERIFIED（platform#176）
  const [email, setEmail] = useState('');
  const [acked, setAcked] = useState(false);
  const partner = h.built_by_partner.name || t('handover.partner_fallback');
  const builder = h.builder.name || t('handover.builder_fallback');
  const customer = h.customer?.name || t('handover.customer_fallback');
  const v = { partner, builder, customer, org: h.org_name || org.display_name };
  const refresh = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ['kmgr'] }), qc.invalidateQueries({ queryKey: ['me-orgs'] }), qc.invalidateQueries({ queryKey: ['me-devices'] })]);
  };
  const run = async (key: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(key);
    setErr(null);
    setMsg(null);
    try {
      await fn();
      if (done) setMsg(done);
      await refresh();
    } catch (e) {
      if (!verify.ask(e)) setErr(handoverErr(t, e, v));
    } finally {
      setBusy(null);
    }
  };
  const invite = (e: FormEvent) => {
    e.preventDefault();
    const mail = email.trim().toLowerCase();
    if (!mail) return;
    void run(
      'invite',
      async () => {
        await orgApi.invite(org, { email: mail, role: 'org_admin', handover: true });
        setEmail('');
      },
      t('handover.invited', { email: mail }),
    );
  };
  const transfer = () =>
    run('owner', async () => {
      if (!h.customer) return;
      if (!(await ask({ danger: true, title: t('handover.owner_confirm_title', v), body: t('handover.owner_confirm_body', v), action: t('handover.owner_btn', v) }))) return;
      await orgsApi.ownerTransfer(org, h.customer.id, org.version);
    });
  const requestSign = () =>
    run('sign_req', async () => {
      const r = await handoverApi.requestSign(org.api, org.org_id);
      setMsg(r.sent ? t('handover.sign_sent', v) : t('handover.sign_wait', { time: stamp(r.next_at).slice(11) }));
    });
  const signAdd = () =>
    run('sign', async () => {
      const ks = await kmgrApi.keysets(org.api, org.org_id);
      const cand = ks.candidates.find((c) => c.user_id === h.customer?.id);
      if (!cand) throw new Error(t('handover.no_candidate', v));
      const cur = ks.current;
      const next = { org_id: cur.org_id, version: cur.version + 1, keys: [...cur.keys, cand] };
      if (cur.keys.length === 0) {
        await kmgrApi.changeKeyset(org.api, org.org_id, { version: next.version, change: 'add', keys: next.keys });
        return;
      }
      if (!config.sign_url) throw new Error(t('wizard.passkey_no_sign'));
      const mine = cur.keys.filter((k) => k.user_id === h.builder.id).map((k) => k.cred_id);
      const a = await requestSignature<KeysetAssertion>(config.sign_url, 'keyset', { keyset: next, previous: cur, cred_ids: mine });
      await kmgrApi.changeKeyset(org.api, org.org_id, { version: next.version, change: 'add', keys: next.keys, signed_by: a.cred_id, assertion: a });
    });
  const removeMine = () =>
    run('remove', async () => {
      if (!(await ask({ danger: true, title: t('handover.remove_confirm_title'), body: t('handover.remove_confirm_body', { ...v, n: h.builder_tokens.length }), action: t('handover.remove_btn') })))
        return;
      const ks = await kmgrApi.keysets(org.api, org.org_id);
      const cur = ks.current;
      await kmgrApi.changeKeyset(org.api, org.org_id, { version: cur.version + 1, change: 'remove', keys: cur.keys.filter((k) => k.user_id !== h.builder.id), remove_reason: 'handover' });
    });
  const revokeNow = () =>
    run('revoke', async () => {
      if (!(await ask({ danger: true, title: t('handover.revoke_title', { n: h.builder_tokens.length }), body: t('handover.revoke_body', v), action: t('handover.revoke_btn') }))) return;
      for (const tok of h.builder_tokens) await kmgrApi.revokeDeployToken(org.api, tok.home_id, tok.token_id);
    });
  const decide = (step: 'delegation' | 'template', decision: 'skip' | 'keep' | 'revert') =>
    run('step_' + step, async () => {
      if (decision === 'revert' && !(await ask({ title: t('handover.revert_title'), body: t('handover.revert_body', { n: h.templates.length }), action: t('handover.revert_btn') }))) return;
      if (decision === 'skip' && !(await ask({ title: t('handover.skip_title'), body: t('handover.skip_body', v), action: t('handover.skip_btn') }))) return;
      await handoverApi.step(org.api, org.org_id, step, decision);
    });
  const leave = () =>
    run('leave', async () => {
      if (!(await ask({ danger: true, title: t('handover.leave_title', v), body: t('handover.leave_body', v), action: t('handover.leave_btn') }))) return;
      await orgApi.removeMember(org, 'me', true);
      await Promise.all([qc.invalidateQueries({ queryKey: ['me-orgs'] }), qc.invalidateQueries({ queryKey: ['me-devices'] })]);
      nav('/org/kmgr/built', { replace: true });
    });

  const dpaDone = h.steps.some((s) => s.step === 'dpa' && s.state === 'done');
  const wait = (s: HandoverStep) => (s.actor === 'builder' ? t('handover.wait_builder', v) : t('handover.wait_customer', v));
  const action = (s: HandoverStep): ReactNode => {
    if (s.state === 'done' || s.state === 'skipped' || s.state === 'waiting') return null;
    if (!myTurn(h, s)) return <span className="muted km-sub">{wait(s)}</span>;
    switch (s.step) {
      case 'owner':
        return h.customer ? (
          <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => void transfer()} data-testid="km-ho-owner">
            {t('handover.owner_btn', v)}
          </button>
        ) : (
          <span className="muted km-sub">{t('handover.owner_need_customer')}</span>
        );
      case 'dpa':
        return (
          <Link className="btn btn-primary btn-sm" to="/org/kmgr/enable?step=1" data-testid="km-ho-dpa">
            {t('handover.dpa_btn')}
          </Link>
        );
      case 'delegation':
        return (
          <span className="toolbar wrap" style={{ margin: 0 }}>
            <Link className="btn btn-primary btn-sm" to="/people/external" data-testid="km-ho-deleg">
              {t('handover.deleg_btn', v)}
            </Link>
            <button className="btn btn-sm" disabled={!!busy} onClick={() => void decide('delegation', 'skip')} data-testid="km-ho-deleg-skip">
              {t('handover.skip_btn')}
            </button>
          </span>
        );
      case 'template':
        return (
          <span className="toolbar wrap" style={{ margin: 0 }}>
            <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => void decide('template', 'keep')} data-testid="km-ho-tpl-keep">
              {t('handover.tpl_keep')}
            </button>
            <button className="btn btn-sm" disabled={!!busy} onClick={() => void decide('template', 'revert')} data-testid="km-ho-tpl-revert">
              {t('handover.revert_btn')}
            </button>
          </span>
        );
      case 'customer_key':
        return (
          <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => void signAdd()} data-testid="km-ho-sign">
            {t('handover.sign_btn', v)}
          </button>
        );
      case 'builder_key':
        return (
          <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => void removeMine()} data-testid="km-ho-remove">
            {t('handover.remove_btn')}
          </button>
        );
      case 'builder_left':
        return (
          <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => void leave()} data-testid="km-ho-leave">
            {t('handover.leave_btn')}
          </button>
        );
    }
    return null;
  };
  // ⑤ 客戶這邊：代建者要簽，客戶先〔請〈代建者〉加入你的金鑰〕
  const customerKeyStep = h.steps.find((s) => s.step === 'customer_key');
  const showRequest = h.viewer === 'customer' && customerKeyStep?.state === 'todo';
  const tokensStep = h.steps.find((s) => s.step === 'builder_key');
  const showTokens = h.builder_tokens.length > 0 && (tokensStep?.state === 'todo' || h.steps.find((s) => s.step === 'builder_left')?.state === 'todo');

  return (
    <div data-testid="km-handover" data-viewer={h.viewer}>
      {dialog}
      {verify.prompt}
      <p className="muted" data-testid="km-ho-intro">
        {h.viewer === 'customer' ? t('handover.intro_customer', v) : t('handover.intro_builder', v)} · {t('handover.remaining', { n: h.remaining })}
      </p>
      {!dpaDone && (
        <div className="km-bar warn" role="status" data-testid="km-ho-dpa-bar">
          <span className="ic" aria-hidden>
            ⚠
          </span>
          <span className="txt">{t('handover.dpa_bar')}</span>
        </div>
      )}
      {h.keyset.exists && h.keyset.empty && (
        <div className="km-bar bad" role="status" data-testid="km-ho-empty-keyset">
          <span className="ic" aria-hidden>
            ✕
          </span>
          <span className="txt">{t('handover.empty_keyset')}</span>
        </div>
      )}
      {err && (
        <p className="error" role="alert" data-testid="km-ho-err">
          {err}
        </p>
      )}
      {msg && (
        <p className="notice card" role="status" data-testid="km-ho-msg">
          {msg}
        </p>
      )}
      {h.viewer !== 'customer' && !h.customer && (
        <section className="card" style={{ marginBottom: 'var(--space-3)' }} aria-labelledby="km-ho-invite-h">
          <h2 id="km-ho-invite-h" style={{ fontSize: 16, marginTop: 0 }}>
            {t('handover.invite_title')}
          </h2>
          {h.invite ? (
            <p data-testid="km-ho-invite-pending">{t('handover.invite_pending', { email: h.invite.email, until: stamp(h.invite.expires_at).slice(0, 10) })}</p>
          ) : (
            <p className="muted km-sub">{t('handover.invite_body')}</p>
          )}
          <form className="toolbar wrap" onSubmit={invite} style={{ margin: 0 }}>
            <label className="km-sr" htmlFor="km-ho-email">
              {t('handover.invite_email')}
            </label>
            <input id="km-ho-email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('handover.invite_email')} />
            <button className="btn btn-primary" disabled={!!busy || !email.trim()} data-testid="km-ho-invite">
              {h.invite ? t('handover.invite_again') : t('handover.invite_btn')}
            </button>
          </form>
        </section>
      )}
      <ol className="km-ho-steps card" data-testid="km-ho-steps">
        {h.steps.map((s, i) => (
          <li key={s.step} data-step={s.step} data-state={s.state} className={'km-ho-step ' + s.state}>
            <span className="ic" aria-hidden>
              {ICON[s.state]}
            </span>
            <span className="txt">
              <strong>
                {i + 1}. {t(`handover.step_${s.step}`, v)}
              </strong>
              <span className="muted km-sub"> · {t(`handover.state_${s.state}`)}</span>
              {s.state !== 'done' && s.state !== 'skipped' && <span className="km-sub muted km-ho-why">{t(`handover.why_${s.step}`, v)}</span>}
              {s.step === 'template' && h.templates.length > 0 && s.state === 'todo' && (
                <ul className="plain-list km-sub" data-testid="km-ho-templates">
                  {h.templates.map((x) => (
                    <li key={x.home_id}>{t('handover.tpl_row', { home: x.home_name || x.home_id, name: x.template_name, v: x.version })}</li>
                  ))}
                </ul>
              )}
            </span>
            <span className="act">{action(s)}</span>
          </li>
        ))}
      </ol>
      {showRequest && (
        <section className="card" style={{ marginTop: 'var(--space-3)' }} data-testid="km-ho-request">
          <p>{t('handover.request_body', v)}</p>
          {h.sign_requested_at && <p className="muted km-sub">{t('handover.request_sent_at', { time: stamp(h.sign_requested_at), ...v })}</p>}
          <div className="toolbar wrap" style={{ margin: 0 }}>
            <button className="btn btn-primary" disabled={!!busy} onClick={() => void requestSign()} data-testid="km-ho-request-btn">
              {t('handover.request_btn', v)}
            </button>
            <Link className="btn" to="/org/kmgr/keys">
              {t('handover.register_key')}
            </Link>
          </div>
        </section>
      )}
      {showTokens && (
        <section className="card" style={{ marginTop: 'var(--space-3)' }} data-testid="km-ho-tokens" aria-labelledby="km-ho-tokens-h">
          <h2 id="km-ho-tokens-h" style={{ fontSize: 16, marginTop: 0 }}>
            {t('handover.tokens_title', { n: h.builder_tokens.length, ...v })}
          </h2>
          <ul className="plain-list">
            {h.builder_tokens.map((x) => (
              <li key={x.token_id}>{t('handover.token_row', { name: x.name, until: stamp(x.expires_at).slice(0, 10), n: x.remaining })}</li>
            ))}
          </ul>
          <p className="km-sub">{t('handover.tokens_note', v)}</p>
          {h.viewer === 'customer' && !acked && (
            <div className="toolbar wrap" style={{ margin: 0 }}>
              <button className="btn" onClick={() => setAcked(true)} data-testid="km-ho-tokens-ack">
                {t('handover.tokens_ack')}
              </button>
              <button className="btn btn-danger" disabled={!!busy} onClick={() => void revokeNow()} data-testid="km-ho-tokens-revoke">
                {t('handover.revoke_btn')}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/** 錯誤的白話（HANDOVER_INCOMPLETE 照 detail.reason；CONFLICT 的接手原因）。 */
export function handoverErr(t: (k: string, o?: Record<string, unknown>) => string, e: unknown, v: Record<string, string>): string {
  if (isKinioError(e)) {
    const reason = typeof e.detail?.reason === 'string' ? e.detail.reason : '';
    if (e.code === 'HANDOVER_INCOMPLETE' && reason) return t(`handover.incomplete_${reason}`, v);
    if (e.code === 'CONFLICT' && reason) return t(`handover.conflict_${reason}`, { ...v, defaultValue: errText(t, e) });
    if (e.code === 'INVALID_ARGUMENT' && reason === 'not_handover_customer') return t('handover.not_handover_customer', v);
    return errText(t, e);
  }
  return e instanceof Error ? e.message : String(e);
}
